import {
    Button,
    Caption,
    ProductInfo as AriaProductInfo,
    useAppDisplayModeContext,
    useStyles,
    pxToRem,
} from '@archipro-design/aria';
import { Link, useFetcher } from '@remix-run/react';
import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import type { ModuleProduct } from '@modules/page-builder/api/get-module-product.server';
import type { PageBuilderGetModuleProductAction } from '~/routes/remix-api.page-builder.get-module-product';
import { QUERY_MODULE_PRODUCT } from '~/routes/remix-api.page-builder.get-module-product';
import VariantDot from '@modules/tile/component/product-tile/VariantDot';
import { AriaProductInfoStyle } from '@modules/page-builder/component/common/product-info/ProductInfo.style';
import type { ColumnDimensionType } from '@modules/page-builder/types';
import { SingleImageWithProductContainer } from '@modules/page-builder/component/detail/detail-single-image-shell/DetailSingleImageShell.style';
import { fetcherIsDone } from '~/utils/fetcherHelper';

interface ProductInfoProps {
    productId: number;
    variant?: '01' | '02';
    image?: ReactNode;
    clickable: boolean;
    dimension?: ColumnDimensionType;
}

const ProductInfo = ({
    productId,
    variant,
    image,
    clickable,
    dimension,
}: ProductInfoProps) => {
    const { mobile } = useAppDisplayModeContext();
    const fetcher = useFetcher<PageBuilderGetModuleProductAction>();
    const { css } = useStyles({ dimension, variant });

    const [productInfo, setProductInfo] = useState<ModuleProduct>();

    const fetchProductInfo = () => {
        if (!productInfo) {
            fetcher.submit(
                { productId: productId.toString() },
                {
                    method: 'get',
                    action: QUERY_MODULE_PRODUCT,
                    preventScrollReset: true,
                }
            );
        }
    };

    useEffect(() => {
        fetchProductInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fetcherIsDone(fetcher) && fetcher.data) {
            setProductInfo(fetcher.data);
        }
    }, [fetcher]);

    const { productLink, variants, ...rest } = productInfo || {};

    const variantDots =
        variants && variants.length > 1
            ? variants.map(({ id, label, code, dot }) => ({
                  key: `variant-dot-${id}`,
                  children: (
                      <VariantDot
                          size={mobile ? 9 : 13}
                          colorCode={code ?? ''}
                          description={label}
                          image={dot?.[mobile ? 'mobile' : 'desktop']}
                      />
                  ),
              }))
            : undefined;

    const cta =
        clickable && productLink ? (
            <Button
                as={Link}
                to={productLink}
                size={14}
                variables={{
                    buttonPadding: pxToRem(9),
                    buttonSize14TextFontSize: pxToRem(12),
                }}
            >
                VIEW PRODUCT
            </Button>
        ) : (
            <Button
                size={14}
                variables={{
                    buttonPadding: pxToRem(9),
                    buttonSize14TextFontSize: pxToRem(12),
                }}
            >
                VIEW PRODUCT
            </Button>
        );

    const innerContent = productInfo ? (
        <>
            {image}
            <AriaProductInfo
                {...rest}
                className={css(AriaProductInfoStyle)}
                productName={
                    <Caption variant="04" maximumLines={1}>
                        {productInfo.productName}
                    </Caption>
                }
                professionalName={{
                    children: (
                        <Caption variant="04" maximumLines={1}>
                            {productInfo.professionalName}
                        </Caption>
                    ),
                }}
                variants={variantDots}
                variant={variant || '01'}
                {...(variant === '02' ? { cta } : {})}
            />
        </>
    ) : null;

    return (
        <div className={css(SingleImageWithProductContainer)}>
            {clickable ? (
                <Link to={productInfo?.productLink || ''}>{innerContent}</Link>
            ) : (
                innerContent
            )}
        </div>
    );
};

export default ProductInfo;
